<script setup lang="ts">
import { useScroll } from '@vueuse/core';
import { computed, useTemplateRef, watch } from 'vue';

import type {
  HotelFilter,
  HotelFilterUpdate,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { DEFAULT_HOTEL_FILTER_UPDATE } from '@apps/main/new-desktop/modules/hotelFilters';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import type { AppMaskType } from '@libs/ui';
import { AppMask, AppTag, arrivedStateToMaskType } from '@libs/ui';

import { useHotelFiltersStore } from './store/hotelFiltersStore';

interface HotelSelectionFiltersTagsProps {
  ingredient?: HotelIngredient | OwnStayIngredient | null;
}

const props = defineProps<HotelSelectionFiltersTagsProps>();

const hotelFiltersStore = useHotelFiltersStore();

interface HotelFiltersTagsEmits {
  (
    eventName: 'update-filter',
    updateEvent: { type: HotelFilterUpdate; id?: string }
  ): void;
}

const emit = defineEmits<HotelFiltersTagsEmits>();

watch(
  () => props.ingredient,
  () => {
    if (props.ingredient) {
      hotelFiltersStore.setFiltersValuesFromIngredient(props.ingredient);
    }
  },
  { immediate: true, deep: true }
);

const pillsContainerRef = useTemplateRef('pillsContainerRef');

const { arrivedState } = useScroll(pillsContainerRef, {
  behavior: 'smooth',
});

const maskType = computed<AppMaskType>(() =>
  arrivedStateToMaskType(arrivedState)
);

function onUpdateFilter(type: HotelFilter, id?: string): void {
  emit('update-filter', { type: DEFAULT_HOTEL_FILTER_UPDATE[type], id });
}
</script>

<template>
  <AppMask :type="maskType">
    <div ref="pillsContainerRef" class="hotel-selection-filters-pills">
      <AppTag
        v-for="pill of hotelFiltersStore.activeFilters(props.ingredient)"
        :key="pill.type"
        class="tag"
        type="filter"
        :icon="{
          name: 'closeInCircle',
          size: '18',
          position: 'right',
          color: 'color-neutral-70',
          hoverColor: 'color-neutral-70',
          clickable: true,
          ariaLabel: $t('a11y.remove_filter'),
        }"
        :text-wrap="false"
        @icon-click="onUpdateFilter(pill.type, pill.id)"
      >
        <template #title>{{ pill.text }}</template>
      </AppTag>
    </div>
  </AppMask>
</template>

<style scoped lang="scss">
.hotel-selection-filters-pills {
  --section-height: 40px;

  display: flex;
  gap: var(--spacing-8);
  overflow-x: hidden;
  height: var(--section-height);
  padding-bottom: var(--spacing-8);

  overflow-y: hidden;

  @include on-hover {
    overflow-x: auto;
  }

  @include for-touch-devices {
    overflow-x: auto;
  }

  .tag {
    height: var(--spacing-32);
  }
}
</style>
